import { useEffect, useMemo, useRef, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import { useHistory, useLocation } from 'react-router-dom'
import { CameraIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import Input from 'components/atoms/Input'
import LoadingIcon from 'components/atoms/LoadingIcon'
import notify from 'components/atoms/Notify'
import Selection from 'components/molecules/Selection'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiDelete from 'lib/network/apiDelete'
import { getLocalizedCountries } from 'pages/company/modals/countries'
import validations from 'validations'
import _, { find, includes, isArray, isObject, isString, split, startCase, trim } from 'lodash'
import Switch from 'components/atoms/Switch'
import moment from 'moment-timezone'
import Blockies from 'react-blockies'
import CryptoJS from 'crypto-js'
import UserWalletLogs from './modals/UserLogs'
import { Buffer } from 'buffer'
import AddBalance from './modals/AddBalanceModel'
import useModal from 'hooks/useModal'
import { DownloadIcon, EyeIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import DocumentMagicLink from 'components/molecules/DocumentMagicLink'

const normalizeToIdArray = value => {
  if (!value) return []
  if (isArray(value)) {
    return value.map(item => (isObject(item) && !isArray(item) && item?._id != null ? String(item._id) : String(item))).filter(Boolean)
  }
  if (isString(value)) {
    return split(value, ',')
      .map(item => trim(item))
      .filter(Boolean)
  }
  return []
}

const normalizeToId = value => {
  if (!value) return ''
  if (isObject(value) && !isArray(value) && value?._id != null) return String(value._id)
  return String(value)
}

const getRoleTypes = (selectedRoles, allRoles) => {
  if (!selectedRoles?.length) return []

  return selectedRoles
    .map(role => {
      if (isObject(role) && !isArray(role) && role?.type) return role.type
      const roleId = normalizeToId(role)
      return find(allRoles, item => String(item._id) === roleId)?.type
    })
    .filter(Boolean)
}

const hasRoleType = (selectedRoles, allRoles, type) => includes(getRoleTypes(selectedRoles, allRoles), type)

const UserProfile = ({ readOnly, loading, userData, isEdit, id, roles, refetch }) => {
  const [previews, setPreviews] = useState({})
  const [files, setFiles] = useState({})
  const [profile, setProfile] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [companyNames, setCompanyNames] = useState([])
  const [partnerNames, setPartnerNames] = useState([])
  const [branches, setBranches] = useState([])

  // const [params, setParams] = useState({ t: tabs[0].id })
  const [params, setParams] = useState({ t: 1 })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [payoutRequestLoading, setPayoutRequestLoading] = useState(false)
  const history = useHistory()
  const location = useLocation()
  const profileRef = useRef(null)
  const { t, i18n } = useTranslation()
  const localizedCountries = useMemo(() => getLocalizedCountries(i18n.language), [i18n.language])

  const tabs = [
    { id: 1, name: t('userProfile.tabs.userDetail'), href: '#', current: false },
    { id: 2, name: t('userProfile.tabs.walletDetail'), href: '#', current: false },
    { id: 3, name: t('userProfile.tabs.payoutRequest'), href: '#', current: false },
    { id: 4, name: t('userProfile.tabs.userDocuments'), href: '#', current: false },
  ]

  const navigateToUsersList = () => {
    const searchParams = new URLSearchParams(location.search)
    const queryString = searchParams.toString()
    const usersPath = queryString ? `/users?${queryString}` : '/users'
    history.push(usersPath)
  }

  useEffect(() => {
    setPreviews({
      profile: userData?.userProfile?.profile,
      avatar: userData?.avatar,
      cover: userData?.userProfile?.cover,
    })
    setProfile({
      username: userData?.userProfile?.username,
      ...userData?.userProfile?.userKyc,
      twoFactorAuth: userData?.userProfile?.twoFactorAuth,
    })
    if (userData?.company) {
      fetchBranches(userData.company)
    }
  }, [userData])

  useEffect(() => {
    fetchCompanies()
    fetchPartners()
  }, [])

  const fetchPartners = async () => {
    try {
      const response = await apiGet('partners', { pageSize: 1000 })
      if (response?.data?.partners) {
        setPartnerNames(
          response.data.partners.map(partner => ({
            id: partner._id,
            name: partner.name,
          }))
        )
      } else {
        setPartnerNames([])
      }
    } catch (error) {
      setPartnerNames([])
    }
  }

  const fetchCompanies = async () => {
    const response = await apiGet('companies', { pageSize: 1000 })

    if (response?.data?.company) {
      const companies = response.data?.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
  }

  const fetchBranches = async companyId => {
    try {
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        const branchList = response.data.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
        setBranches(branchList)
      } else {
        setBranches([])
      }
    } catch (error) {
      notify.error(t('userProfile.messages.failedToFetchBranches'))
      setBranches([])
    }
  }

  const setCurrentTab = currentTab => setParams({ t: parseInt(currentTab, 10) })

  const handlePayoutRequestAction = async (payoutRequestId, action) => {
    setPayoutRequestLoading(`${payoutRequestId}-${action}`)
    try {
      const response = await apiPost(`verify/payout-request/${payoutRequestId}`, { action })
      if (response.success) {
        notify.success(t(`userProfile.messages.payoutRequest${action === 'confirmed' ? 'Accepted' : 'Rejected'}Successfully`))
        if (refetch) {
          refetch()
        }
      }
    } catch (error) {
      notify.error(t(`userProfile.messages.failedTo${action === 'confirmed' ? 'Accept' : 'Reject'}PayoutRequest`))
      console.error('Payout request action error:', error)
    } finally {
      setPayoutRequestLoading(false)
    }
  }

  const addUpdateUser = async (values, { setSubmitting }) => {
    const data = {
      fullName: values.fullName,
      email: values.email,
      phone: values.phone,
      company: values.company,
      branch: values.branch,
      partner: values.partner,
      birthDate: values.birthDate,
      nationality: values.nationality,
      verified: values.verified,

      phoneVerified: values.phoneVerified,
      emailVerified: values.emailVerified,
      signUpComplete: values.signUpComplete,
      active: values.active,
      isDeleted: values.isDeleted,
      resident: values.resident,
      roles: values.roles,
      blacklisted: values.blacklisted,
      superCompanyUser: values.superCompanyUser,
      canManageBranding: values.canManageBranding,
      canManageDns: values.canManageDns,
    }

    delete data.id
    delete data.avatar

    const isCompany = isCompanyVisible(data?.roles)
    const isPartner = isPartnerVisible(data?.roles)

    if (!isCompany) {
      delete data.company
      delete data.branch
      data.canManageBranding = false
      data.canManageDns = false
    }

    if (!isPartner) {
      delete data.partner
    }

    if (isPartner && !data.partner) {
      notify.error(t('userProfile.messages.partnerRequired', { defaultValue: 'Partner is required for partner role users' }))
      setSubmitting(false)
      return
    }

    setIsSubmitting(true)
    const formData = new FormData()

    if (files.cover) formData.append('cover', files.cover)
    if (files.profile) formData.append('profile', files.profile)
    if (files.avatar) formData.append('avatar', files.avatar)

    // eslint-disable-next-line
    Object.keys(data).forEach(key => {
      if (data[key] === 'null' || data[key] === undefined || data[key] === null) delete data[key]
      // eslint-disable-next-line
      if (typeof data[key] === 'string' || typeof data[key] === 'number' || typeof data[key] === 'boolean' || Array.isArray(data[key])) {
        formData.append(key, data[key])
      }
      // eslint-disable-next-line
      if (typeof data[key] === 'object' && !Array.isArray(data[key])) formData.append(key, JSON.stringify(data[key]))
    })
    // eslint-disable-next-line
    Object.keys(profile).forEach(key => {
      if (profile[key] === 'null' || profile[key] === undefined || profile[key] === null) delete profile[key]
      // eslint-disable-next-line
      if (typeof profile[key] === 'string' || typeof profile[key] === 'number' || typeof profile[key] === 'boolean' || Array.isArray(profile[key])) {
        formData.append(key, profile[key])
      }
    })

    try {
      if (isEdit) {
        const response = await apiPut(`users/edit/${id}`, formData)
        if (response.success) {
          notify.success(t('userProfile.messages.userUpdatedSuccessfully'))
          navigateToUsersList()
        }
      } else {
        const response = await apiPost('user', formData)
        if (response.success) {
          notify.success(t('userProfile.messages.userAddedSuccessfully'))
          navigateToUsersList()
        }
      }
    } catch (error) {
      notify.error(t('userProfile.messages.failedToSaveUser'))
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  const deleteUser = () => {
    notify.deleteConfirm({
      title: t('userProfile.messages.deleteUserTitle'),
      message: t('userProfile.messages.deleteUserMessage'),
      onConfirm: async () => {
        const response = await apiDelete(`user/delete/${id}`)
        if (response?.success) {
          navigateToUsersList()
          notify.success(t('userProfile.messages.userDeletedSuccessfully'))
        }
      },
    })
  }

  const showPreview = (key, file) => {
    const preview = URL.createObjectURL(file)
    setPreviews({ ...previews, [key]: preview })
  }

  const uploadDocument = async (file, documentType) => {
    try {
      const formData = new FormData()
      formData.append('document', file)
      formData.append('documentType', documentType)

      const response = await apiPost(`upload-documents/${id}`, formData)
      if (response.success) {
        notify.success(t('userProfile.messages.documentUploadedSuccessfully', { documentType }))
        if (refetch) {
          refetch()
        }
      }
    } catch (error) {
      notify.error(t('userProfile.messages.failedToUploadDocument', { documentType }))
      console.error('Document upload error:', error)
    }
  }

  const updateDocumentVerification = async (documentType, isVerified) => {
    if (!isVerified) {
      notify.deleteConfirm({
        title: t('userProfile.messages.confirmRejectDocumentTitle', { documentType }),
        message: t('userProfile.messages.confirmRejectDocumentMessage', { documentType }),
        confirmText: t('userProfile.buttons.reject'),
        cancelText: t('userProfile.buttons.cancel'),
        t,
        onConfirm: async () => {
          await processDocumentVerification(documentType, isVerified)
        },
      })
    } else {
      await processDocumentVerification(documentType, isVerified)
    }
  }

  const processDocumentVerification = async (documentType, isVerified) => {
    try {
      const payload = {}

      if (documentType === 'idCard') {
        payload.isIdCardVerified = isVerified
      } else if (documentType === 'drivingLicense') {
        payload.isDrivingLicenseVerified = isVerified
      } else if (documentType === 'passport') {
        payload.isPassportVerified = isVerified
      }

      const response = await apiPost(`verify-upload-documents/${id}`, payload)

      if (response.success) {
        notify.success(t('userProfile.messages.documentVerificationUpdatedSuccessfully', { documentType }))
        if (refetch) refetch()
      }
    } catch (error) {
      notify.error(t('userProfile.messages.failedToUpdateDocumentVerification', { documentType }))
      console.error('Document verification update error:', error)
    }
  }

  const initialValues = isEdit
    ? {
        ...userData,
        roles: normalizeToIdArray(userData?.roles),
        company: normalizeToId(userData?.company),
        branch: normalizeToIdArray(userData?.branch),
        partner: normalizeToId(userData?.partner),
      }
    : {
        roles: ['user'],
        verified: false,
        phoneVerified: false,
        emailVerified: false,
        signUpComplete: false,
        isDeleted: false,
        resident: false,
        active: false,
        superCompanyUser: false,
        canManageBranding: false,
        canManageDns: false,
      }

  const rolesList = roles?.map(role => ({ id: String(role._id), name: startCase(role.name) }))

  const isCompanyVisible = selectedRoles => hasRoleType(selectedRoles, roles, 'company')

  const isPartnerVisible = selectedRoles => hasRoleType(selectedRoles, roles, 'partner')
  const closeDrawer = () => {
    setIsDrawerOpen(false)
  }

  const handleAddUserBalanceSuccess = () => {
    closeModal()
    refetch()
  }

  const [AddUserBalanceModal, openAddUserBalanceModel, closeModal] = useModal({
    content: <AddBalance userId={id} onSuccess={handleAddUserBalanceSuccess} />,
    title: t('userProfile.modal.addBalance'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  // Document preview states and modal
  const [previewDocument, setPreviewDocument] = useState({ url: '', title: '', type: '' })

  const openDocumentPreview = (documentUrl, documentTitle, documentType = 'image') => {
    setPreviewDocument({ url: documentUrl, title: documentTitle, type: documentType })
    openPreviewModal()
  }

  // Fullscreen modal for images
  const [fullscreenImage, setFullscreenImage] = useState({ url: '', title: '' })

  // Define modal content as a component to avoid initialization issues
  const FullscreenImageContent = () => {
    const handleImageClick = () => {
      closeFullscreenModal()
    }

    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        {/* <img
          src={fullscreenImage.url}
          alt={fullscreenImage.title}
          className="max-w-full max-h-full object-contain cursor-zoom-out"
          onClick={handleImageClick}
          onError={e => {
            e.target.style.display = 'none'
            e.target.nextSibling.style.display = 'block'
          }}
        /> */}
        <div onClick={handleImageClick} role="button" tabIndex={0} className="flex h-full w-full items-center justify-center">
          <img
            src={fullscreenImage.url}
            alt={fullscreenImage.title}
            className="max-w-full max-h-full object-contain cursor-zoom-out"
            onError={e => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
        </div>

        <div style={{ display: 'none' }} className="text-center text-gray-500 p-8">
          <p>{t('userProfile.modal.imageNotAvailable')}</p>
        </div>
      </div>
    )
  }

  const [FullscreenModal, openFullscreenModal, closeFullscreenModal] = useModal({
    content: <FullscreenImageContent />,
    title: fullscreenImage.title,
    dimensions: {
      height: '95vh',
      width: '95vw',
    },
    closeable: true,
  })

  const openFullscreenImage = (imageUrl, imageTitle) => {
    setFullscreenImage({ url: imageUrl, title: imageTitle })
    openFullscreenModal()
  }

  const [PreviewModal, openPreviewModal] = useModal({
    content: (
      <div className="flex flex-col items-center justify-center">
        {previewDocument.type === 'pdf' ? (
          <embed src={previewDocument.url} width="100%" height="500px" type="application/pdf" className="border rounded" />
        ) : (
          <img
            src={previewDocument.url}
            alt={previewDocument.title}
            className="max-w-full max-h-[70vh] object-contain"
            onError={e => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
        )}
        <div style={{ display: 'none' }} className="text-center text-gray-500 p-8">
          <p>{t('userProfile.modal.previewNotAvailable')}</p>
          <p className="text-sm mt-2">{t('userProfile.modal.downloadFileToView')}</p>
        </div>
      </div>
    ),
    title: t('userProfile.modal.previewDocument', { title: previewDocument.title }),
    dimensions: {
      height: '80vh',
      width: '80vw',
      maxWidth: '900px',
    },
    closeable: true,
  })

  const renderDocumentVerificationStatus = (isVerified, isRejected) => {
    let label = t('userProfile.status.waitingForApproval')
    let color = 'bg-yellow-100 text-yellow-800'

    if (isVerified === true) {
      label = t('userProfile.buttons.accepted')
      color = 'bg-green-100 text-green-800'
    } else if (isRejected === true) {
      label = t('userProfile.buttons.rejected')
      color = 'bg-red-100 text-red-800'
    }

    return <span className={`inline-flex w-44 shrink-0 justify-center whitespace-nowrap px-3 py-1 text-sm font-semibold rounded-full ${color}`}>{label}</span>
  }

  const getPayoutStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <>
      <Formik initialValues={initialValues} validationSchema={isEdit ? null : validations.AddUserSchema} onSubmit={addUpdateUser} enableReinitialize>
        {({ values, isValid, setFieldValue }) =>
          !loading ? (
            <div className="w-full mb-[60px] flex flex-col">
              <div className="bg-white flex w-full">
                <Form className="space-y-8 py-8 w-full mx-auto px-8">
                  <div>
                    <div className="border-b border-gray-200 pb-5 sm:pb-0">
                      <h3 className="text-xl font-medium leading-6 text-gray-900">
                        {t('userProfile.title')} {readOnly ? t('userProfile.readOnly') : ''}
                      </h3>
                      <div className="mt-3 sm:mt-4">
                        <div className="block">
                          <nav className="-mb-px flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-8">
                            {tabs.map(tab => (
                              <span
                                key={tab.name}
                                onClick={() => setCurrentTab(tab.id)}
                                className={classNames(
                                  tab.id === params?.t
                                    ? 'border-l-indigo-500 sm:border-l-transparent sm:border-b-indigo-500 text-indigo-600'
                                    : 'border-l-transparent sm:border-b-transparent text-gray-500 hover:text-gray-700 hover:border-l-gray-300 sm:hover:border-b-gray-300',
                                  'whitespace-nowrap py-2 px-3 sm:py-4 sm:px-0 border-l-4 sm:border-l-0 sm:border-b-2 font-medium text-sm cursor-pointer rounded-r-md sm:rounded-none'
                                )}
                                aria-current={params?.t === tab.id ? 'page' : undefined}
                              >
                                {tab.name}
                              </span>
                            ))}
                          </nav>
                        </div>
                      </div>
                    </div>
                  </div>

                  {params?.t === 1 && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-center sm:pt-5">
                        <label htmlFor="photo" className="block text-sm font-medium text-gray-700">
                          {/* Avatar */}
                          {t('userProfile.form.avatar')}
                        </label>
                        <div className="mt-1 sm:mt-0 sm:col-span-2">
                          <div className="flex pb-4 items-center">
                            <div className="bg-gray relative h-[200px] w-[150px] border mr-4 border-dashed border-gray-300 rounded-md">
                              <input
                                id="avatar"
                                ref={profileRef}
                                name="avatar"
                                className="hidden"
                                accept=".jpg,.jpeg,.png"
                                type="file"
                                onChange={event => {
                                  showPreview('avatar', event.target.files[0])
                                  setFiles({ ...files, avatar: event.target.files[0] })
                                }}
                              />
                              {previews.avatar ? (
                                <span onClick={() => profileRef.current.click()}>
                                  <img src={previews.avatar} alt="" className="cursor-pointer bg-cover h-full w-[150px] rounded" />
                                </span>
                              ) : (
                                <div
                                  onClick={() => profileRef.current.click()}
                                  className="bg-white flex p-2 items-center justify-center bg-contain bg-no-repeat bg-center border-gray-300 rounded-md h-full w-full cursor-pointer"
                                >
                                  <CameraIcon className="text-gray-400 h-8 w-8" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="fullName" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('userProfile.form.displayName')}
                          </label>
                          <Field name="fullName" as={Input} type="text" className="w-full" />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('userProfile.form.emailAddress')}
                          </label>
                          <Field name="email" as={Input} type="text" className="w-full" />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('userProfile.form.phoneNumber')}
                          </label>
                          <Field name="phone" as={Input} type="text" className="w-full" />
                        </div>
                        <div>
                          <label htmlFor="country" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            {t('userProfile.form.roles')}
                          </label>
                          <div className="w-full">
                            <Selection
                              onChange={roleIds => setFieldValue('roles', roleIds || [])}
                              multiple
                              title={t('userProfile.placeholders.selectRoles')}
                              list={rolesList}
                              value={values?.roles}
                            />
                          </div>
                        </div>
                        {isCompanyVisible(values?.roles) && (
                          <>
                            <div>
                              <label htmlFor="company" className="block text-sm font-normal text-gray-700 mb-1">
                                {t('userProfile.form.company')}
                              </label>
                              <Selection
                                onChange={value => {
                                  if (value?.id) {
                                    // Switching company invalidates the previously selected branches.
                                    if (value.id !== values?.company) {
                                      setFieldValue('branch', [])
                                    }
                                    setFieldValue('company', value.id)
                                    fetchBranches(value.id)
                                  }
                                }}
                                title={t('userProfile.placeholders.selectCompanies')}
                                list={companyNames}
                                value={values?.company || ''}
                                className="w-full"
                              />
                            </div>
                            <div>
                              <label htmlFor="branch" className="block text-sm font-normal text-gray-700 mb-1">
                                {t('userProfile.form.branch')}
                              </label>
                              <Selection
                                onChange={branchIds => setFieldValue('branch', branchIds || [])}
                                multiple
                                title={t('userProfile.placeholders.selectBranch')}
                                list={branches}
                                value={values?.branch}
                                className="w-full"
                              />
                            </div>
                          </>
                        )}

                        {isPartnerVisible(values?.roles) && (
                          <div>
                            <label htmlFor="partner" className="block text-sm font-normal text-gray-700 mb-1">
                              {t('userProfile.form.partner', { defaultValue: 'Partner' })}
                            </label>
                            <Selection
                              onChange={value => {
                                if (value?.id) {
                                  setFieldValue('partner', value.id)
                                }
                              }}
                              title={t('userProfile.placeholders.selectPartner', { defaultValue: 'Select partner' })}
                              list={partnerNames}
                              value={values?.partner || ''}
                              className="w-full"
                            />
                          </div>
                        )}

                        <div>
                          <label htmlFor="birthDate" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('userProfile.form.dob')}
                          </label>
                          <Field name="birthDate" as={Input} value={values.birthDate ? moment(values.birthDate).format('YYYY-MM-DD') : ''} type="date" className="w-full" />
                        </div>
                        <div>
                          <label htmlFor="nationality" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('userProfile.form.nationality')}
                          </label>
                          <Selection
                            onChange={value => {
                              if (value && value.id && values.nationality === value.id) {
                                setFieldValue('nationality', '')
                              } else if (value && value.id) {
                                setFieldValue('nationality', value.id)
                              }
                            }}
                            title={t('userProfile.placeholders.selectNationality')}
                            list={localizedCountries}
                            value={values?.nationality || ''}
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div className="mb-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-5">
                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap"> {t('userProfile.form.verified')}</p>
                            <Switch
                              id="verified"
                              value={values.verified}
                              onChange={checked => {
                                setFieldValue('verified', checked)
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.phoneVerified')}</p>
                            <Switch
                              id="phoneVerified"
                              value={values.phoneVerified}
                              onChange={checked => {
                                setFieldValue('phoneVerified', checked)
                              }}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.active')}</p>
                            <Switch
                              id="active"
                              value={values.active}
                              onChange={checked => {
                                setFieldValue('active', checked)
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mb-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-5">
                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.emailVerified')}</p>
                            <Switch
                              id="emailVerified"
                              value={values.emailVerified}
                              onChange={checked => {
                                setFieldValue('emailVerified', checked)
                              }}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.signUpComplete')}</p>
                            <Switch
                              id="signUpComplete"
                              value={values.signUpComplete}
                              onChange={checked => {
                                setFieldValue('signUpComplete', checked)
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.deleted')}</p>
                            <Switch
                              id="isDeleted"
                              value={values.isDeleted}
                              onChange={checked => {
                                setFieldValue('isDeleted', checked)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mb-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-5">
                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.blacklisted')}</p>
                            <Switch
                              id="blacklisted"
                              value={values.blacklisted}
                              onChange={checked => {
                                setFieldValue('blacklisted', checked)
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.resident')}</p>
                            <Switch
                              id="resident"
                              value={values.resident}
                              onChange={checked => {
                                setFieldValue('resident', checked)
                              }}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <p className="whitespace-nowrap">{t('userProfile.form.superCompanyUser')}</p>
                            <Switch
                              id="superCompanyUser"
                              value={values.superCompanyUser}
                              onChange={checked => {
                                setFieldValue('superCompanyUser', checked)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      {isCompanyVisible(values.roles) && (
                        <div className="mb-6">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-5">
                            <div className="grid grid-cols-2 gap-2">
                              <p className="whitespace-nowrap">{t('userProfile.form.canManageBranding')}</p>
                              <Switch
                                id="canManageBranding"
                                value={values.canManageBranding}
                                onChange={checked => {
                                  setFieldValue('canManageBranding', checked)
                                }}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <p className="whitespace-nowrap">{t('userProfile.form.canManageDns')}</p>
                              <Switch
                                id="canManageDns"
                                value={values.canManageDns}
                                onChange={checked => {
                                  setFieldValue('canManageDns', checked)
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {params?.t === 2 && (
                    <>
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="name" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            {t('userProfile.form.balance')}
                          </label>
                          <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center sm:mt-0 w-full max-w-lg sm:col-span-2 gap-2">
                            <span>{userData?.wallet?.balance}</span>
                            <div className="flex gap-2">
                              <button
                                onClick={openAddUserBalanceModel}
                                type="button"
                                className="inline-flex items-center px-2 py-1 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                              >
                                {t('userProfile.buttons.updateBalance')}
                              </button>
                              <button
                                onClick={() => setIsDrawerOpen(true)}
                                type="button"
                                className="inline-flex items-center px-2 py-1 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                              >
                                {t('userProfile.buttons.viewStatement')}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="name" className="block text-sm font-bold text-normal text-gray-700 sm:mt-px sm:pt-2">
                            {t('userProfile.form.currency')}
                          </label>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">{userData?.wallet?.currency}</div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="name" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            {t('userProfile.form.walletAddress')}
                          </label>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">{userData?.wallet?.walletAddress}</div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="name" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            {t('userProfile.form.createdAt')}
                          </label>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                            {userData?.wallet?.createdAt ? moment(userData.wallet.createdAt).format('DD MMMM YYYY, hh:mm A') : ''}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="name" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            {t('userProfile.form.updatedAt')}
                          </label>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                            {userData?.wallet?.updatedAt ? moment(userData.wallet.updatedAt).format('DD MMMM YYYY, hh:mm A') : ''}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {params?.t === 3 && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <div className="space-y-6">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          <h4 className="text-lg font-medium text-gray-900">{t('userProfile.payoutRequests.title')}</h4>
                          {userData?.payoutRequest && userData.payoutRequest.length > 0 && (
                            <span className="inline-flex px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-full">
                              {t('userProfile.payoutRequests.totalRequests', { count: userData.payoutRequest.length })}
                            </span>
                          )}
                        </div>

                        {userData?.payoutRequest && userData.payoutRequest.length > 0 ? (
                          <div className="grid gap-4">
                            {userData.payoutRequest.map((request, index) => (
                              <div key={request._id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-4">
                                      <h5 className="text-lg font-semibold text-gray-900">{t('userProfile.payoutRequests.requestNumber', { number: index + 1 })}</h5>
                                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPayoutStatusColor(request.status)}`}>
                                        {/* eslint-disable-next-line */}
                                        {request.status?.toUpperCase()}
                                      </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                      <div>
                                        <span className="text-sm font-medium text-gray-500">{t('userProfile.payoutRequests.amount')}</span>
                                        <p className="text-lg font-semibold text-gray-900">${request.payoutBalance}</p>
                                      </div>
                                      <div>
                                        <span className="text-sm font-medium text-gray-500">{t('userProfile.payoutRequests.requestedDate')}</span>
                                        <p className="text-sm text-gray-900">{request.createdAt ? moment(request.createdAt).format('DD MMMM YYYY') : ''}</p>
                                        <p className="text-xs text-gray-500">{request.createdAt ? moment(request.createdAt).format('hh:mm A') : ''}</p>
                                      </div>
                                      <div>
                                        <span className="text-sm font-medium text-gray-500">{t('userProfile.payoutRequests.lastUpdated')}</span>
                                        <p className="text-sm text-gray-900">{request.updatedAt ? moment(request.updatedAt).format('DD MMMM YYYY') : ''}</p>
                                        <p className="text-xs text-gray-500">{request.updatedAt ? moment(request.updatedAt).format('hh:mm A') : ''}</p>
                                      </div>
                                    </div>

                                    {request.status === 'pending' && !readOnly && (
                                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-3 border-t border-gray-200">
                                        <button
                                          onClick={() => handlePayoutRequestAction(request._id, 'confirmed')}
                                          disabled={payoutRequestLoading === `${request._id}-confirmed`}
                                          type="button"
                                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {payoutRequestLoading === `${request._id}-confirmed` ? t('userProfile.buttons.processing') : t('userProfile.buttons.acceptRequest')}
                                        </button>
                                        <button
                                          onClick={() => handlePayoutRequestAction(request._id, 'rejected')}
                                          disabled={payoutRequestLoading === `${request._id}-rejected`}
                                          type="button"
                                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {payoutRequestLoading === `${request._id}-rejected` ? t('userProfile.buttons.processing') : t('userProfile.buttons.rejectRequest')}
                                        </button>
                                      </div>
                                    )}

                                    {request.status !== 'pending' && (
                                      <div className="pt-3 border-t border-gray-200">
                                        <span className="text-sm text-gray-500">
                                          {t('userProfile.payoutRequests.requestStatus', {
                                            status: request.status,
                                            date: moment(request.updatedAt).format('DD MMMM YYYY, hh:mm A'),
                                          })}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <div className="mx-auto h-12 w-12 text-gray-400">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('userProfile.payoutRequests.noPayoutRequests')}</h3>
                            <p className="mt-1 text-sm text-gray-500">{t('userProfile.payoutRequests.noPayoutRequestsDescription')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {params?.t === 4 && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <DocumentMagicLink user={userData} userId={id} />
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="idCard" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('userProfile.form.idCard')}
                        </label>
                        <div className="mt-1 flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-4 sm:mt-0 sm:col-span-2">
                          <input
                            type="file"
                            name="idCard"
                            accept=".jpg,.jpeg,.png,.webp,.pdf,.document,.docx,.xls,.xlsx,.plain"
                            className="w-full sm:w-64"
                            onChange={event => {
                              const file = event.target.files[0]
                              if (file) {
                                setFiles({ ...files, idCard: file })
                                // eslint-disable-next-line
                                if (file.type.startsWith('image/')) {
                                  showPreview('idCard', file)
                                }
                                uploadDocument(file, 'idCard')
                              }
                            }}
                          />
                          {userData?.idCard && (
                            <>
                              <div className="flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-4 w-full sm:w-auto">
                                {/* eslint-disable-next-line */}
                                {userData.idCard?.toLowerCase().includes('.pdf') ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openDocumentPreview(userData.idCard, t('userProfile.form.idCard'), 'pdf')}
                                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      <EyeIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                                      {t('userProfile.buttons.previewPdf')}
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    className="relative shrink-0 cursor-pointer border-2 border-gray-300 rounded-lg overflow-hidden hover:border-blue-500 transition-colors"
                                    onClick={() => openFullscreenImage(userData.idCard, t('userProfile.form.idCard'))}
                                  >
                                    <img
                                      src={userData.idCard}
                                      alt="ID Card Preview"
                                      className="h-32 w-48 object-contain bg-gray-50 hover:opacity-90 transition-opacity"
                                      onError={e => {
                                        e.target.style.display = 'none'
                                        e.target.nextSibling.style.display = 'flex'
                                      }}
                                    />
                                    <div style={{ display: 'none' }} className="h-32 w-48 flex items-center justify-center bg-gray-100">
                                      <span className="text-gray-500 text-sm">{t('userProfile.modal.imageNotAvailable')}</span>
                                    </div>
                                  </div>
                                )}
                                <a
                                  href={userData.idCard}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  <DownloadIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                                  {t('userProfile.buttons.download')}
                                </a>
                              </div>
                              <div className="flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-2 w-full sm:w-auto sm:ml-auto">
                                <p className="whitespace-nowrap">{t('userProfile.status.idCardStatus')}</p>
                                {renderDocumentVerificationStatus(userData?.isIdCardVerified, userData?.isIdCardRejected)}
                                <div className="flex shrink-0 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => updateDocumentVerification('idCard', true)}
                                    className={`px-3 py-1 rounded text-sm font-medium ${
                                      userData?.isIdCardVerified === true ? 'bg-dark-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                                    }`}
                                  >
                                    {t('userProfile.buttons.accept')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateDocumentVerification('idCard', false)}
                                    className={`px-3 py-1 rounded text-sm font-medium ${
                                      userData?.isIdCardRejected === true ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                                    }`}
                                  >
                                    {t('userProfile.buttons.reject')}
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="drivingLicense" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('userProfile.form.drivingLicense')}
                        </label>
                        <div className="mt-1 flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-4 sm:mt-0 sm:col-span-2">
                          <input
                            type="file"
                            name="drivingLicense"
                            accept=".jpg,.jpeg,.png,.webp,.pdf,.document,.docx,.xls,.xlsx,.plain"
                            className="w-full sm:w-64"
                            onChange={event => {
                              const file = event.target.files[0]
                              if (file) {
                                setFiles({ ...files, drivingLicense: file })
                                // eslint-disable-next-line
                                if (file.type.startsWith('image/')) {
                                  showPreview('drivingLicense', file)
                                }
                                uploadDocument(file, 'drivingLicense')
                              }
                            }}
                          />
                          {userData?.drivingLicense && (
                            <>
                              <div className="flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-4 w-full sm:w-auto">
                                {/* eslint-disable-next-line */}
                                {userData.drivingLicense?.toLowerCase().includes('.pdf') ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openDocumentPreview(userData.drivingLicense, t('userProfile.form.drivingLicense'), 'pdf')}
                                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      <EyeIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                                      {t('userProfile.buttons.previewPdf')}
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    className="relative shrink-0 cursor-pointer border-2 border-gray-300 rounded-lg overflow-hidden hover:border-blue-500 transition-colors"
                                    onClick={() => openFullscreenImage(userData.drivingLicense, t('userProfile.form.drivingLicense'))}
                                  >
                                    <img
                                      src={userData.drivingLicense}
                                      alt="Driving License Preview"
                                      className="h-32 w-48 object-contain bg-gray-50 hover:opacity-90 transition-opacity"
                                      onError={e => {
                                        e.target.style.display = 'none'
                                        e.target.nextSibling.style.display = 'flex'
                                      }}
                                    />
                                    <div style={{ display: 'none' }} className="h-32 w-48 flex items-center justify-center bg-gray-100">
                                      <span className="text-gray-500 text-sm">{t('userProfile.modal.imageNotAvailable')}</span>
                                    </div>
                                  </div>
                                )}
                                <a
                                  href={userData.drivingLicense}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  <DownloadIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                                  {t('userProfile.buttons.download')}
                                </a>
                              </div>
                              <div className="flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-2 w-full sm:w-auto sm:ml-auto">
                                <p className="whitespace-nowrap">{t('userProfile.status.licenseStatus')}</p>
                                {renderDocumentVerificationStatus(userData?.isDrivingLicenseVerified, userData?.isDrivingLicenseRejected)}
                                <div className="flex shrink-0 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => updateDocumentVerification('drivingLicense', true)}
                                    className={`px-3 py-1 rounded text-sm font-medium ${
                                      userData?.isDrivingLicenseVerified === true ? 'bg-dark-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                                    }`}
                                  >
                                    {t('userProfile.buttons.accept')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateDocumentVerification('drivingLicense', false)}
                                    className={`px-3 py-1 rounded text-sm font-medium ${
                                      userData?.isDrivingLicenseRejected === true ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                                    }`}
                                  >
                                    {t('userProfile.buttons.reject')}
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="passport" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('userProfile.form.passport')}
                        </label>
                        <div className="mt-1 flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-4 sm:mt-0 sm:col-span-2">
                          <input
                            type="file"
                            name="passport"
                            accept=".jpg,.jpeg,.png,.webp,.pdf,.document,.docx,.xls,.xlsx,.plain"
                            className="w-full sm:w-64"
                            onChange={event => {
                              const file = event.target.files[0]
                              if (file) {
                                setFiles({ ...files, passport: file })
                                // eslint-disable-next-line
                                if (file.type.startsWith('image/')) {
                                  showPreview('passport', file)
                                }
                                uploadDocument(file, 'passport')
                              }
                            }}
                          />
                          {userData?.passport && (
                            <>
                              <div className="flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-4 w-full sm:w-auto">
                                {/* eslint-disable-next-line */}
                                {userData.passport?.toLowerCase().includes('.pdf') ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openDocumentPreview(userData.passport, t('userProfile.form.passport'), 'pdf')}
                                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      <EyeIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                                      {t('userProfile.buttons.previewPdf')}
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    className="relative shrink-0 cursor-pointer border-2 border-gray-300 rounded-lg overflow-hidden hover:border-blue-500 transition-colors"
                                    onClick={() => openFullscreenImage(userData.passport, t('userProfile.form.passport'))}
                                  >
                                    <img
                                      src={userData.passport}
                                      alt="Passport Preview"
                                      className="h-32 w-48 object-contain bg-gray-50 hover:opacity-90 transition-opacity"
                                      onError={e => {
                                        e.target.style.display = 'none'
                                        e.target.nextSibling.style.display = 'flex'
                                      }}
                                    />
                                    <div style={{ display: 'none' }} className="h-32 w-48 flex items-center justify-center bg-gray-100">
                                      <span className="text-gray-500 text-sm">{t('userProfile.modal.imageNotAvailable')}</span>
                                    </div>
                                  </div>
                                )}
                                <a
                                  href={userData.passport}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  <DownloadIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                                  {t('userProfile.buttons.download')}
                                </a>
                              </div>
                              <div className="flex flex-col sm:flex-row flex-wrap min-w-0 items-start sm:items-center gap-2 w-full sm:w-auto sm:ml-auto">
                                <p className="whitespace-nowrap">{t('userProfile.status.passportStatus')}</p>
                                {renderDocumentVerificationStatus(userData?.isPassportVerified, userData?.isPassportRejected)}
                                <div className="flex shrink-0 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => updateDocumentVerification('passport', true)}
                                    className={`px-3 py-1 rounded text-sm font-medium ${
                                      userData?.isPassportVerified === true ? 'bg-dark-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                                    }`}
                                  >
                                    {t('userProfile.buttons.accept')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateDocumentVerification('passport', false)}
                                    className={`px-3 py-1 rounded text-sm font-medium ${
                                      userData?.isPassportRejected === true ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                                    }`}
                                  >
                                    {t('userProfile.buttons.reject')}
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {params?.t !== 2 && params?.t !== 3 && params?.t !== 4 && !readOnly && (
                    <div className="pt-2 pb-4">
                      <div className="flex flex-col sm:flex-row justify-between gap-3">
                        <button
                          type="submit"
                          disabled={!isValid || isSubmitting}
                          className={classNames(
                            (!isValid || isSubmitting) && 'cursor-not-allowed opacity-50',
                            'w-full sm:w-auto order-2 sm:order-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500'
                          )}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              <span>Please wait...</span>
                            </span>
                          ) : (
                            <span>{!isEdit ? 'Add' : 'Update'}</span>
                          )}
                        </button>

                        {isEdit && (
                          <button
                            type="button"
                            onClick={deleteUser}
                            className="w-full sm:w-auto order-1 sm:order-2 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Delete User
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </Form>
                <UserWalletLogs isDrawerOpen={isDrawerOpen} closeDrawer={closeDrawer} selectedUser={userData} />
              </div>
            </div>
          ) : (
            <div className="w-full h-[500px] flex items-center justify-center">
              <LoadingIcon />
            </div>
          )
        }
      </Formik>
      <AddUserBalanceModal />
      <PreviewModal />
      <FullscreenModal />
    </>
  )
}

export default UserProfile
